import {useTooltip} from 'components/Tooltip/hook';
import useDebounce from 'hooks/useDebounce';
import useOutsideDetector from 'hooks/useOutsideDetector';
import {uniq} from 'lodash';
import {useCallback, useEffect, useRef, useState} from 'react';

import focusRef from '../helpers';
import {MultiChoice, OptionsLocal, OptionsServer, SingleChoice} from '../type';
import useOptions from './useOptions';

type Props = {
    disabled?: boolean;
    hasClearSelection?: boolean;
    isMultiChoices?: boolean;
    isSearchable?: boolean;
    readonly?: boolean;
    type: 'input' | 'select';
} & (MultiChoice | SingleChoice) &
    (OptionsLocal | OptionsServer);

const useDropdown = ({
    disabled,
    hasClearSelection,
    isMultiChoices,
    isSearchable,
    onChange,
    options: propsOptions,
    readonly,
    serverRequest,
    type,
    value,
}: Props) => {
    // References
    const refField = useRef<HTMLDivElement>(null);
    const refDrawer = useRef<HTMLDivElement>(null);

    // Use States
    const [isShown, setIsShown] = useState(() => false);

    const [searchQuery, setSearchQuery] = useState('');
    const searchQueryDebounced = useDebounce(searchQuery, 300);

    // Use Options
    const {
        isLoading,
        isOptionSelected,
        options,
        saveAndSortCachedOptions,
        selectedOptions,
    } = useOptions({
        isSearchable,
        isShown,
        searchQuery,
        searchQueryDebounced,
        ...({isMultiChoices, onChange, value} as MultiChoice | SingleChoice),
        ...({options: propsOptions, serverRequest} as
            | OptionsLocal
            | OptionsServer),
    });

    const {setTooltipRef} = useTooltip();

    const openDropdown = useCallback(() => {
        setIsShown(true);
    }, []);

    const closeDropdown = useCallback(() => {
        setIsShown(false);
        setSearchQuery('');
    }, []);

    const toggleDropdown = useCallback(() => {
        if (isShown) {
            return closeDropdown();
        }

        return openDropdown();
    }, [closeDropdown, isShown, openDropdown]);

    useOutsideDetector([refField, refDrawer], () => closeDropdown());

    // Add key listeners
    const onUserKeyPress = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                focusRef(refField);
                closeDropdown();
            }
        },
        [closeDropdown],
    );

    useEffect(() => {
        window.addEventListener('keydown', onUserKeyPress);

        return () => {
            window.removeEventListener('keydown', onUserKeyPress);
        };
    }, [onUserKeyPress]);

    // Focus the searchable field of the drawer
    const focusInput = useCallback(() => {
        refDrawer.current?.querySelector('input')?.focus({
            preventScroll: true,
        });
    }, []);

    // Blur the searchable field of the drawer
    const blurInput = useCallback(() => {
        refDrawer.current?.querySelector('input')?.blur();
    }, []);

    useEffect(() => {
        if (isShown) {
            setTimeout(() => focusInput(), 100);
        } else blurInput();
    }, [blurInput, focusInput, isShown]);

    // onClicks
    const onClickField = useCallback(() => {
        if (!disabled && !readonly) {
            toggleDropdown();
        }
    }, [disabled, readonly, toggleDropdown]);

    const onClickClearSelection = useCallback(() => {
        if (!onChange) return;

        if (isMultiChoices) {
            onChange([]);
        } else if (type === 'input') {
            onChange('');
        } else {
            onChange(undefined);
        }

        if (!isSearchable && !isMultiChoices) {
            closeDropdown();
            saveAndSortCachedOptions(undefined);
        }
    }, [
        closeDropdown,
        isMultiChoices,
        isSearchable,
        onChange,
        saveAndSortCachedOptions,
        type,
    ]);

    const onClickOption = useCallback(
        (clickedValue: string) => {
            if (!onChange) return;

            // If click for multi-select
            if (isMultiChoices) {
                const currentValues = value || [];

                let newValues: string[];

                if (currentValues.includes(clickedValue)) {
                    newValues = currentValues.filter(
                        (optionValue) => optionValue !== clickedValue,
                    );
                } else {
                    newValues = uniq([...currentValues, clickedValue]);
                }

                onChange(newValues);
            }
            // If click for single-select
            else {
                onChange(clickedValue);
                closeDropdown();
                saveAndSortCachedOptions(clickedValue);
            }
        },
        [
            closeDropdown,
            isMultiChoices,
            onChange,
            saveAndSortCachedOptions,
            value,
        ],
    );

    return {
        blurInput,
        focusInput,
        hasClearSelection,
        isLoading,
        isMultiChoices,
        isOptionSelected,
        isSearchable,
        isShown,
        onClickClearSelection,
        onClickField,
        onClickOption,
        onUserKeyPress,
        options,
        refDrawer,
        refField,
        searchQuery,
        searchQueryDebounced,
        selectedOptions,
        setIsShown,
        setSearchQuery,
        setTooltipRef,
        type,
        value,
    };
};

export default useDropdown;
